import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import { createOrder } from '../api/orders';
import { useTelegram } from '../hooks/useTelegram';

const eo: [number, number, number, number] = [0, 0, 0.25, 1];

const OrderPage: React.FC = () => {
  const navigate = useNavigate();
  const { showBackButton, hapticImpact, hapticNotification } = useTelegram();

  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const cleanup = showBackButton(() => navigate(-1));
    return cleanup;
  }, [showBackButton, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim() || !contact.trim() || !message.trim()) {
      setError('Пожалуйста, заполните все поля');
      hapticNotification('error');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      await createOrder({ name: name.trim(), contact: contact.trim(), message: message.trim() });
      hapticNotification('success');
      setSuccess(true);
    } catch (err) {
      setError('Не удалось отправить заявку. Попробуйте позже.');
      hapticNotification('error');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const inputBaseStyle: React.CSSProperties = {
    width: '100%',
    padding: '14px 16px',
    borderRadius: '10px',
    border: '1px solid rgba(0,0,0,0.12)',
    background: '#FEFEFE',
    color: '#111111',
    fontFamily: "'Instrument Sans', -apple-system, sans-serif",
    fontSize: '15px',
    outline: 'none',
    WebkitAppearance: 'none',
    boxSizing: 'border-box' as const,
    transition: 'border-color 0.25s cubic-bezier(0.25,0.1,0.25,1)',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '12px',
    fontWeight: 600,
    letterSpacing: '0.04em',
    color: '#6E6E73',
    marginBottom: '8px',
    textTransform: 'uppercase',
  };

  if (success) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.45, ease: eo }}
        style={{ minHeight: '100vh', background: 'transparent' }}
      >
        <Header showBack />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '80px 24px',
            textAlign: 'center',
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.55, ease: eo }}
            style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: '#F6F6F7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '24px',
            }}
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#111"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                strokeDasharray: 24,
                strokeDashoffset: 24,
                animation: 'checkDraw 0.5s cubic-bezier(0,0,0.25,1) 0.3s both',
              }}
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.12, ease: eo }}
            style={{
              fontFamily: "'DM Serif Display', Georgia, serif",
              fontSize: '24px',
              color: '#111111',
              marginBottom: '8px',
              letterSpacing: '-0.02em',
            }}
          >
            Отлично, заявка у нас!
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.2, ease: eo }}
            style={{
              fontSize: '15px',
              color: '#6E6E73',
              marginBottom: '32px',
              lineHeight: 1.5,
            }}
          >
            Ответим в течение 24 часов.<br />Обычно быстрее.
          </motion.div>

          <motion.button
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.28, ease: eo }}
            onClick={() => {
              hapticImpact('light');
              navigate('/');
            }}
            style={{
              padding: '14px 32px',
              borderRadius: '14px',
              border: '1px solid rgba(0,0,0,0.12)',
              background: 'transparent',
              color: '#111111',
              fontFamily: "'Instrument Sans', -apple-system, sans-serif",
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            Вернуться
          </motion.button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 24, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.45, ease: eo }}
      style={{ minHeight: '100vh', background: 'transparent' }}
    >
      <Header showBack />

      <div style={{ padding: '32px 24px' }}>
        <motion.h1
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, ease: eo }}
          style={{
            fontFamily: "'DM Serif Display', Georgia, serif",
            fontSize: '28px',
            lineHeight: 1.15,
            letterSpacing: '-0.03em',
            color: '#111111',
            marginBottom: '8px',
          }}
        >
          Давайте обсудим<br />ваш проект
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, delay: 0.06, ease: eo }}
          style={{
            fontSize: '15px',
            lineHeight: 1.5,
            color: '#6E6E73',
            marginBottom: '32px',
          }}
        >
          Расскажите, что нужно — оценим стоимость и сроки бесплатно
        </motion.p>

        <form onSubmit={handleSubmit}>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.12, ease: eo }}
            style={{ marginBottom: '20px' }}
          >
            <label style={labelStyle}>Имя</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Как к вам обращаться"
              required
              style={inputBaseStyle}
              onFocus={(e) => { e.target.style.borderColor = '#111111'; }}
              onBlur={(e) => { e.target.style.borderColor = 'rgba(0,0,0,0.12)'; }}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.2, ease: eo }}
            style={{ marginBottom: '20px' }}
          >
            <label style={labelStyle}>Контакт</label>
            <input
              type="text"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              placeholder="@telegram, email или телефон"
              required
              style={inputBaseStyle}
              onFocus={(e) => { e.target.style.borderColor = '#111111'; }}
              onBlur={(e) => { e.target.style.borderColor = 'rgba(0,0,0,0.12)'; }}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.28, ease: eo }}
            style={{ marginBottom: '20px' }}
          >
            <label style={labelStyle}>Задача</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Опишите задачу: бот, сайт, приложение — что нужно сделать?"
              required
              style={{
                ...inputBaseStyle,
                resize: 'vertical',
                minHeight: '120px',
                lineHeight: 1.5,
              }}
              onFocus={(e) => { e.target.style.borderColor = '#111111'; }}
              onBlur={(e) => { e.target.style.borderColor = 'rgba(0,0,0,0.12)'; }}
            />
          </motion.div>

          {error && (
            <motion.p
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              style={{ color: '#EF4444', fontSize: '13px', margin: '0 0 12px', fontWeight: 500 }}
            >
              {error}
            </motion.p>
          )}

          <motion.button
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.38, ease: eo }}
            type="submit"
            disabled={submitting}
            style={{
              width: '100%',
              padding: '16px',
              borderRadius: '14px',
              border: 'none',
              background: '#1D1D1F',
              color: '#FEFEFE',
              fontFamily: "'Instrument Sans', -apple-system, sans-serif",
              fontSize: '15px',
              fontWeight: 600,
              marginTop: '8px',
              letterSpacing: '-0.01em',
              cursor: submitting ? 'not-allowed' : 'pointer',
              opacity: submitting ? 0.7 : 1,
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            {submitting ? 'Отправляем...' : 'Получить оценку бесплатно'}
          </motion.button>
        </form>
      </div>
    </motion.div>
  );
};

export default OrderPage;
